import type { SortingState } from '@tanstack/react-table';
import { parseAsSortingState } from '@utils/sorting.util';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORTING: SortingState = [{ desc: false, id: 'name' }];

type ParentPortfolioQueryParams = {
  limit: number;
  page: number;
  search?: string;
};

type UseParentPortfolioQueryParamsReturn = {
  DEFAULT_SORTING: SortingState;
  handleLimitChange: (newLimit: number) => void;
  limit: number;
  page: number;
  queryParams: ParentPortfolioQueryParams;
  search: string | null;
  setPage: (page: number) => Promise<URLSearchParams>;
  setSearch: (search: string | null) => Promise<URLSearchParams>;
  setSorting: (sorting: SortingState | null) => Promise<URLSearchParams>;
  sortingState: SortingState;
};

export const useParentPortfolioQueryParams = (): UseParentPortfolioQueryParamsReturn => {
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(DEFAULT_PAGE_SIZE));
  const [search, setSearch] = useQueryState('search', parseAsString);
  const [sorting, setSorting] = useQueryState('sorting', parseAsSortingState.withDefault(DEFAULT_SORTING));

  const sortingState: SortingState = sorting ?? DEFAULT_SORTING;

  const queryParams = useMemo(() => {
    return {
      limit: limit ?? DEFAULT_PAGE_SIZE,
      page: page ?? 1,
      search: search ?? undefined,
    };
  }, [limit, page, search]);

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      void setLimit(newLimit);
      void setPage(1);
    },
    [setLimit, setPage],
  );

  return {
    DEFAULT_SORTING,
    handleLimitChange,
    limit: limit ?? DEFAULT_PAGE_SIZE,
    page: page ?? 1,
    queryParams,
    search,
    setPage,
    setSearch,
    setSorting,
    sortingState,
  };
};
